import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleAlert, CircleCheck, SlidersHorizontal } from 'lucide-react';
import { ORDER_ALL, SUMMARY_LANGUAGES, orderFor, savePreferences } from '../utils/preferences';
import './DisplayPreferences.css';

// « Affichage »: the signed-in person's own display preferences — the view
// the dashboard opens on, the order of each status, the language of ticket
// summaries. Saved on each change; employees have theirs.
const DisplayPreferences = ({ views = [], statuses = [], preferences, getViewId, onSaved }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null); // null | 'saving' | 'saved' | 'error'
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const save = async (update) => {
    setState('saving');
    try {
      const saved = await savePreferences(update);
      setState('saved');
      if (onSaved) onSaved(saved, update);
    } catch (error) {
      console.error('Display preferences not saved:', error);
      setState('error');
    }
  };

  const prefs = preferences || { default_view_id: null, ticket_order: {}, summary_language: null };
  const viewIds = views.map((view) => String(getViewId(view))).filter(Boolean);
  const defaultView = prefs.default_view_id && viewIds.includes(String(prefs.default_view_id))
    ? String(prefs.default_view_id) : '';
  const rows = [{ value: ORDER_ALL, label: t('displayPrefs.allStatuses', 'Tous les statuts') }, ...statuses];

  return (
    <div className="dprefs" ref={rootRef}>
      <button
        type="button"
        className={`u-btn u-btn--secondary u-btn--sm${open ? ' dprefs__trigger--open' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={t('displayPrefs.title', "Préférences d'affichage")}
      >
        <SlidersHorizontal aria-hidden="true" />
        <span>{t('displayPrefs.button', 'Affichage')}</span>
      </button>
      {open && (
        <div className="dprefs__panel" role="dialog" aria-label={t('displayPrefs.title', "Préférences d'affichage")}>
          <div className="dprefs__head">
            <div>
              <div className="dprefs__title">{t('displayPrefs.title', "Préférences d'affichage")}</div>
              <p className="dprefs__note">{t('displayPrefs.personal', 'Ces réglages sont les vôtres : vos collègues gardent les leurs.')}</p>
            </div>
            <span className={`dprefs__state${state ? ` dprefs__state--${state}` : ''}`} role="status">
              {state === 'saved' && <CircleCheck aria-hidden="true" />}
              {state === 'error' && <CircleAlert aria-hidden="true" />}
              {state === 'saving' && t('displayPrefs.saving', 'Enregistrement…')}
              {state === 'saved' && t('displayPrefs.saved', 'Enregistré')}
              {state === 'error' && t('displayPrefs.error', 'Non enregistré — réessayez')}
            </span>
          </div>

          <div className="u-field">
            <label className="u-label" htmlFor="dprefs-default-view">{t('displayPrefs.defaultView', 'Vue par défaut')}</label>
            <select
              id="dprefs-default-view"
              className="u-select"
              value={defaultView}
              onChange={(event) => save({ default_view_id: event.target.value || null })}
            >
              <option value="">{t('general', 'Général')}</option>
              {views.map((view) => {
                const id = getViewId(view);
                return id ? <option key={id} value={String(id)}>{view.name}</option> : null;
              })}
            </select>
          </div>

          <div className="u-field">
            <span className="u-label">{t('displayPrefs.order', "Ordre d'affichage")}</span>
            <ul className="dprefs__orders">
              {rows.map(({ value, label }) => {
                const current = orderFor(prefs, value);
                const own = Boolean(prefs.ticket_order && prefs.ticket_order[value]);
                return (
                  <li key={value} className={`dprefs__order${value === ORDER_ALL ? ' dprefs__order--all' : ''}`}>
                    <span
                      className={`dprefs__order-label${own || value === ORDER_ALL ? '' : ' is-inherited'}`}
                      title={label}
                    >
                      {label}
                    </span>
                    <div className="u-seg dprefs__seg" role="group" aria-label={label}>
                      {['desc', 'asc'].map((order) => (
                        <button
                          key={order}
                          type="button"
                          className={`u-seg__btn${current === order ? ' is-active' : ''}`}
                          aria-pressed={current === order}
                          title={order === 'desc'
                            ? t('displayPrefs.newestHint', 'Les plus récemment mis à jour d’abord')
                            : t('displayPrefs.oldestHint', 'Les plus anciens d’abord')}
                          onClick={() => { if (!own || current !== order) save({ ticket_order: { [value]: order } }); }}
                        >
                          {order === 'desc' ? t('displayPrefs.newest', 'Récents') : t('displayPrefs.oldest', 'Anciens')}
                        </button>
                      ))}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="u-field">
            <label className="u-label" htmlFor="dprefs-summary-language">
              {t('displayPrefs.summaryLanguage', 'Langue des résumés de ticket')}
            </label>
            <select
              id="dprefs-summary-language"
              className="u-select"
              value={prefs.summary_language || ''}
              onChange={(event) => event.target.value && save({ summary_language: event.target.value })}
            >
              {!prefs.summary_language && <option value="">{t('displayPrefs.languageDefault', 'Par défaut (français)')}</option>}
              {SUMMARY_LANGUAGES.map((code) => (
                <option key={code} value={code}>{t(`displayPrefs.language_${code}`, code.toUpperCase())}</option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayPreferences;
